import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import { OrderHistoryProductModel } from "@store/Types/ApiModels";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { v4 as uuidv4 } from "uuid";

interface OwnProps {
    onClose: () => void;
    onProductSelected: (selectedProduct: OrderHistoryProductModel) => void;
    isOpen: boolean;
    extendedStyles?: AddNewProductToRmaStyles;
}

const mapStateToProps = (state: any) => {
    return {
        cdnPrefix: getSettingsCollection(state).websiteSettings.cdnPrefix,
    };
};

interface AddNewProductToRmaStyles {
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    productNumberGridItem?: GridItemProps;
    productNumberField?: TextFieldProps;
    productNameGridItem?: GridItemProps;
    productNameField?: TextFieldProps;
    actionButtonsGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;
export const addNewProductToRmaStyles: AddNewProductToRmaStyles = {
    productNumberGridItem: {
        width: 12,
    },
    productNumberField: {
        labelPosition: "left",
        labelProps: {
            css: css`
                width: 20%;
                display: inline-flex;
            `,
        },
    },
    productNameGridItem: {
        width: 12,
    },
    productNameField: {
        labelPosition: "left",
        labelProps: {
            css: css`
                width: 20%;
                display: inline-flex;
            `,
        },
    },
    actionButtonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-top: 30px;
        `,
    },
};

const AddNewProductToRmaModal = ({ onClose, onProductSelected, isOpen, extendedStyles, cdnPrefix }: Props) => {
    const [styles] = useState<AddNewProductToRmaStyles>(() => mergeToNew(addNewProductToRmaStyles, extendedStyles));
    const [productNumber, setProductNumber] = useState<string | null>(null);
    const [productName, setProductName] = useState<string | null>(null);
    const [isValid, setIsValid] = useState<boolean>(false);

    useEffect(() => {
        setIsValid(productNumber !== null && productNumber !== "");
    }, [productNumber]);

    const resetForm = () => {
        setProductNumber(null);
        setProductName(null);
    };
    const handleClose = useCallback(() => {
        resetForm();
        onClose();
    }, [onClose]);

    const handleSubmit = useCallback(() => {
        const product = {
            lineId: "",
            uri: "",
            qtyOrdered: 0,
            maxQtyOrdered: 0,
            autocompleteItemModel: {
                name: `${productName ?? ""}`,
                erpNumber: `*${productNumber!}*`,
                image: `${cdnPrefix}/userfiles/gtech-horizontal-bilingue-noir.png`,
                manufacturerItemNumber: "",
                brandName: "",
                brandDetailPagePath: "",
                subtitle: "",
                title: "",
                url: "",
                isNameCustomerOverride: false,
                styleParentId: null,
                id: uuidv4(),
                uri: "",
                rmaNeedsSn: false,
                properties: {},
            },
            properties: {},
        };

        handleClose();
        onProductSelected(product);
    }, [onProductSelected, handleClose, productName, productNumber]);

    return (
        <Modal
            {...styles.modal}
            headline={translate("Add product information")}
            isOpen={isOpen}
            handleClose={handleClose}
        >
            <GridContainer {...styles.container}>
                <GridItem {...styles.productNumberGridItem}>
                    <TextField
                        {...styles.productNumberField}
                        name="productNumber"
                        maxLength={20}
                        label={translate("Product Number")}
                        value={productNumber ?? ""}
                        onChange={evt => setProductNumber(evt.target.value)}
                    />
                </GridItem>
                <GridItem {...styles.productNameGridItem}>
                    <TextField
                        {...styles.productNameField}
                        name="productName"
                        maxLength={100}
                        label={translate("Product Name")}
                        value={productName ?? ""}
                        onChange={evt => setProductName(evt.target.value)}
                    />
                </GridItem>
                <GridItem {...styles.actionButtonsGridItem}>
                    <Button {...styles.submitButton} disabled={!isValid} onClick={handleSubmit}>
                        {translate("Add product to RMA request")}
                    </Button>
                </GridItem>
            </GridContainer>
        </Modal>
    );
};

export default connect(mapStateToProps)(AddNewProductToRmaModal);
